// reconcileEngine — the single reconciliation primitive shared by every front.
//
// save-conflict (409), draft-drift, multiplayer join, dev connect, AI sync, and
// any future front all reduce to the same problem: two file trees diverged.
// Each front supplies { mine, theirs, base? }; the engine classifies every
// path, auto-resolves the safe majority, three-way-merges (jsdiff) where a base
// exists, and returns only the residual same-file conflicts for the UI to
// review. One engine, one contract. See docs/RECONCILIATION-STRATEGY.md.
//
// PURE + framework-free: no React, no network, no DOM. Fronts wire the inputs
// and an apply() callback around it.

import { diffArrays } from 'diff';

/** path -> contents. Code files only (assets are reconciled out-of-band). */
export type FileMap = Record<string, string>;

/** How a single path was (or should be) resolved. */
export type Disposition =
  | { kind: 'unchanged'; contents: string } // identical on both sides
  | { kind: 'mine-only'; contents: string } // only I changed/added it
  | { kind: 'theirs-only'; contents: string } // only they changed/added it
  | { kind: 'merged'; contents: string } // both changed, diff3 auto-merged cleanly
  | {
      kind: 'conflict'; // both changed, overlapping (or no base) → needs a choice
      mine: string;
      theirs: string;
      base: string | null;
      /** File contents with git-style <<<<<<< markers (for the "markers" /
       *  "resolve locally" rungs). Present only for real conflicts. */
      markers: string;
    };

export interface ReconcilePlan {
  dispositions: Record<string, Disposition>;
  /** Paths in each auto bucket (informational, for the review UI summary). */
  mineOnly: string[];
  theirsOnly: string[];
  merged: string[];
  /** The only set that needs human input. */
  conflicts: string[];
  hasConflicts: boolean;
}

/** Per-conflict user choice collected by the review UI. `resolved` carries
 *  content the user produced elsewhere (e.g. resolved locally in VS Code). */
export type ConflictChoice =
  | { pick: 'mine' }
  | { pick: 'theirs' }
  | { pick: 'resolved'; contents: string };

// ── three-way merge (proper diff3) ────────────────────────────────────────────
//
// We deliberately do NOT use jsdiff's `merge` to render conflicts. On ADJACENT
// edits it splits one conflict into two blocks whose two sides disagree on how
// many base lines they consumed; reconstructing the file by re-walking the base
// then drifts and DUPLICATES content — it corrupted whole files (bye-👋 bug).
// Instead we run the classic diff3: diff each side against the base (LCS via
// jsdiff.diffArrays), then walk the base merging overlapping change hunks into
// tight regions. A region only conflicts when BOTH sides changed it to
// different content. Proven against overlap/adjacent/clean cases.

type Region = { ok: string[] } | { conflict: { mine: string[]; theirs: string[] } };
interface Hunk {
  bs: number; // base range start (inclusive)
  be: number; // base range end (exclusive)
  rep: string[]; // lines that replace base[bs,be) on this side
}

/** Change hunks of `other` vs `base`: base range [bs,be) replaced by `rep`. */
function hunksFor(base: string[], other: string[]): Hunk[] {
  const parts = diffArrays(base, other);
  const hunks: Hunk[] = [];
  let bi = 0;
  let i = 0;
  while (i < parts.length) {
    const p = parts[i];
    if (!p.added && !p.removed) {
      bi += p.value.length;
      i++;
      continue;
    }
    const bs = bi;
    let rep: string[] = [];
    while (i < parts.length && (parts[i].added || parts[i].removed)) {
      if (parts[i].removed) bi += parts[i].value.length;
      else rep = rep.concat(parts[i].value); // added
      i++;
    }
    hunks.push({ bs, be: bi, rep });
  }
  return hunks;
}

/** Rebuild one side's content across base range [lo,hi), applying its hunks. */
function applyOverRange(base: string[], hunks: Hunk[], lo: number, hi: number): string[] {
  const out: string[] = [];
  let pos = lo;
  for (const h of hunks) {
    if (h.bs > pos) out.push(...base.slice(pos, h.bs));
    out.push(...h.rep);
    pos = h.be;
  }
  if (pos < hi) out.push(...base.slice(pos, hi));
  return out;
}

function eqArr(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Classic diff3 → ordered ok / conflict regions over (mine, base, theirs). */
function diff3Regions(mine: string[], base: string[], theirs: string[]): Region[] {
  const A = hunksFor(base, mine);
  const B = hunksFor(base, theirs);
  const regions: Region[] = [];
  const pushOk = (lines: string[]) => {
    if (!lines.length) return;
    const last = regions[regions.length - 1];
    if (last && 'ok' in last) last.ok.push(...lines);
    else regions.push({ ok: lines.slice() });
  };
  let pos = 0;
  let ai = 0;
  let bi = 0;
  const N = base.length;
  while (pos < N || ai < A.length || bi < B.length) {
    const aStart = ai < A.length ? A[ai].bs : Infinity;
    const bStart = bi < B.length ? B[bi].bs : Infinity;
    const nextStart = Math.min(aStart, bStart);
    if (nextStart === Infinity) {
      pushOk(base.slice(pos));
      break;
    }
    if (pos < nextStart) {
      pushOk(base.slice(pos, nextStart));
      pos = nextStart;
    }
    // Expand the region to cover every change hunk (from EITHER side) that
    // overlaps it, so adjacent edits become one region — never two.
    const lo = nextStart;
    let hi = nextStart;
    const aT: Hunk[] = [];
    const bT: Hunk[] = [];
    let changed = true;
    while (changed) {
      changed = false;
      while (ai < A.length && A[ai].bs <= hi) {
        hi = Math.max(hi, A[ai].be);
        aT.push(A[ai]);
        ai++;
        changed = true;
      }
      while (bi < B.length && B[bi].bs <= hi) {
        hi = Math.max(hi, B[bi].be);
        bT.push(B[bi]);
        bi++;
        changed = true;
      }
    }
    const mineC = applyOverRange(base, aT, lo, hi);
    const theirsC = applyOverRange(base, bT, lo, hi);
    if (!bT.length) pushOk(mineC); // only I changed this region
    else if (!aT.length) pushOk(theirsC); // only they changed it
    else if (eqArr(mineC, theirsC)) pushOk(mineC); // both made the same change
    else {
      // Real conflict — trim the common prefix/suffix so the markers are tight.
      let cp = 0;
      while (cp < mineC.length && cp < theirsC.length && mineC[cp] === theirsC[cp]) cp++;
      let cs = 0;
      while (
        cs < mineC.length - cp &&
        cs < theirsC.length - cp &&
        mineC[mineC.length - 1 - cs] === theirsC[theirsC.length - 1 - cs]
      )
        cs++;
      pushOk(mineC.slice(0, cp));
      regions.push({
        conflict: {
          mine: mineC.slice(cp, mineC.length - cs),
          theirs: theirsC.slice(cp, theirsC.length - cs),
        },
      });
      pushOk(mineC.slice(mineC.length - cs));
    }
    pos = hi;
  }
  return regions;
}

/**
 * Render git-style conflict markers from a TWO-way line diff (no common base).
 * Instead of dumping the whole file as one giant `<<< all-mine === all-theirs >>>`
 * block, this keeps the lines the two sides share as context and wraps only the
 * runs that actually differ — so the merge editor shows tight, individually
 * resolvable conflicts (matching what the inline Compare diff shows).
 */
export function twoWayMarkers(mine: string, theirs: string): string {
  // diffArrays(theirs, mine): removed = only-in-theirs, added = only-in-mine.
  const parts = diffArrays(theirs.split('\n'), mine.split('\n'));
  const out: string[] = [];
  let i = 0;
  while (i < parts.length) {
    if (!parts[i].added && !parts[i].removed) {
      out.push(...parts[i].value); // shared context line(s)
      i++;
      continue;
    }
    // Coalesce a consecutive run of changed parts into ONE conflict block.
    const mineLines: string[] = [];
    const theirLines: string[] = [];
    while (i < parts.length && (parts[i].added || parts[i].removed)) {
      if (parts[i].added) mineLines.push(...parts[i].value);
      else theirLines.push(...parts[i].value);
      i++;
    }
    out.push('<<<<<<< mine (yours)', ...mineLines, '=======', ...theirLines, '>>>>>>> theirs');
  }
  return out.join('\n');
}

/** Three-way merge one file. Returns clean merged text, or a conflict with
 *  git-style marker-rendered content (understood by VS Code's merge editor). */
export function threeWayMergeFile(
  mine: string,
  base: string,
  theirs: string,
): { clean: true; contents: string } | { clean: false; markers: string } {
  try {
    const regions = diff3Regions(mine.split('\n'), base.split('\n'), theirs.split('\n'));
    const out: string[] = [];
    if (!regions.some((r) => 'conflict' in r)) {
      for (const r of regions) if ('ok' in r) out.push(...r.ok);
      return { clean: true, contents: out.join('\n') };
    }
    for (const r of regions) {
      if ('ok' in r) out.push(...r.ok);
      else
        out.push(
          '<<<<<<< mine (yours)',
          ...r.conflict.mine,
          '=======',
          ...r.conflict.theirs,
          '>>>>>>> theirs',
        );
    }
    return { clean: false, markers: out.join('\n') };
  } catch {
    // Defensive: never throw into the reconcile flow — degrade to a granular
    // two-way conflict (not a whole-file block).
    return { clean: false, markers: twoWayMarkers(mine, theirs) };
  }
}

// ── classify ──────────────────────────────────────────────────────────────────

/**
 * Classify every path across `mine` and `theirs` (optionally three-way against
 * `base`). Disjoint changes auto-resolve; same-file changes diff3-merge when a
 * base is available, else become conflicts. Only `conflicts` needs human input.
 */
export function classify(mine: FileMap, theirs: FileMap, base?: FileMap): ReconcilePlan {
  const dispositions: Record<string, Disposition> = {};
  const mineOnly: string[] = [];
  const theirsOnly: string[] = [];
  const merged: string[] = [];
  const conflicts: string[] = [];

  const allPaths = new Set([...Object.keys(mine), ...Object.keys(theirs)]);
  for (const p of allPaths) {
    const m = mine[p];
    const t = theirs[p];
    const hasM = m !== undefined;
    const hasT = t !== undefined;

    if (hasM && !hasT) {
      dispositions[p] = { kind: 'mine-only', contents: m };
      mineOnly.push(p);
    } else if (!hasM && hasT) {
      // A file only they have. With a base we can tell "I deleted it" (present
      // in base, unchanged in theirs) from "they added it" — but v1 keeps the
      // simple, non-destructive behaviour: take theirs. (Deletion semantics are
      // a documented v1 limitation — see RECONCILIATION-STRATEGY.)
      dispositions[p] = { kind: 'theirs-only', contents: t };
      theirsOnly.push(p);
    } else if (m === t) {
      dispositions[p] = { kind: 'unchanged', contents: m };
    } else {
      // Both sides have it and they differ → changed.
      const b = base ? base[p] : undefined;
      if (b !== undefined) {
        const r = threeWayMergeFile(m, b, t);
        if (r.clean) {
          dispositions[p] = { kind: 'merged', contents: r.contents };
          merged.push(p);
          continue;
        }
        dispositions[p] = { kind: 'conflict', mine: m, theirs: t, base: b, markers: r.markers };
      } else {
        // No common ancestor → can't three-way merge, but still render tight
        // per-region conflicts from a two-way diff (not one whole-file block).
        dispositions[p] = {
          kind: 'conflict',
          mine: m,
          theirs: t,
          base: null,
          markers: twoWayMarkers(m, t),
        };
      }
      conflicts.push(p);
    }
  }

  return {
    dispositions,
    mineOnly,
    theirsOnly,
    merged,
    conflicts,
    hasConflicts: conflicts.length > 0,
  };
}

// ── assets (binary — no text merge) ──────────────────────────────────────────
//
// Assets can't diff3: you pick a side. Content-addressing makes the compare
// exact — BUT the identity is the `cas/<hash>` content hash, NOT the full URL.
// The same CAS blob is referenced with different URL PREFIXES on the two sides:
// the web buffer holds a relative same-origin `/api/apps/:id/~asset/cas/<hash>`,
// while `tile dev` streams an absolute `http://host/api/apps/:id/~asset/cas/<hash>`
// (and either may carry a `?token=`). A naive full-string compare therefore flags
// EVERY shared base asset (icons, splash, favicon…) as a both-sides conflict even
// though the bytes are identical. So we compare by extracted hash.
// Classification for dev-mode reconcile (mine = web buffer, theirs = disk):
//   • new on web (absent on disk)          → autoMine  (add it to disk, no prompt)
//   • present on both, SAME hash            → skip (identical bytes)
//   • present on both, DIFFERENT hash       → conflict (same path, different bytes → ask)
// theirs-only assets aren't listed here — the caller already applies disk→buffer
// out-of-band so the preview resolves local files.

export interface AssetConflict {
  path: string;
  mineUrl: string;
  theirsUrl: string;
}

export interface AssetPlan {
  /** New/changed on my side, not present on theirs → take mine (auto). */
  autoMine: string[];
  /** Present on both with different content hashes → needs a Keep-mine/Take-theirs pick. */
  conflicts: AssetConflict[];
}

/** path -> hosted asset URL (a CAS `~asset/cas/<hash>` URL; the hash is identity). */
export type AssetMap = Record<string, string>;

/**
 * Content identity of a hosted asset URL = its CAS hash, so two URLs pointing at
 * the same content-addressed blob compare equal regardless of host/prefix or query
 * string (web's relative `/api/.../~asset/cas/<h>` vs dev's absolute
 * `http://host/api/.../~asset/cas/<h>?token=…`). A non-CAS URL (base64 fallback,
 * a disk path) has no hash to key on, so it falls back to the full string.
 */
export function assetIdentity(url: string): string {
  const m = /(?:~|%7e)asset\/cas\/([^/?#]+)/i.exec(url);
  return m ? m[1].toLowerCase() : url;
}

export function classifyAssets(mine: AssetMap, theirs: AssetMap): AssetPlan {
  const autoMine: string[] = [];
  const conflicts: AssetConflict[] = [];
  for (const [p, url] of Object.entries(mine)) {
    const t = theirs[p];
    if (t === undefined) autoMine.push(p); // web-only asset → add to disk
    else if (assetIdentity(t) !== assetIdentity(url))
      conflicts.push({ path: p, mineUrl: url, theirsUrl: t }); // same path, different bytes
    // same hash → identical bytes, nothing to do
  }
  return { autoMine, conflicts };
}

// ── resolve ────────────────────────────────────────────────────────────────────

/**
 * Produce the final file map from a plan + the user's per-conflict choices.
 * Auto buckets (unchanged/mine-only/theirs-only/merged) apply as classified;
 * conflicts use `choices[path]` (default: keep mine, the least-destructive).
 */
export function resolve(
  plan: ReconcilePlan,
  choices: Record<string, ConflictChoice> = {},
): FileMap {
  const out: FileMap = {};
  for (const [p, d] of Object.entries(plan.dispositions)) {
    switch (d.kind) {
      case 'unchanged':
      case 'mine-only':
      case 'theirs-only':
      case 'merged':
        out[p] = d.contents;
        break;
      case 'conflict': {
        const c = choices[p];
        if (c?.pick === 'theirs') out[p] = d.theirs;
        else if (c?.pick === 'resolved') out[p] = c.contents;
        else out[p] = d.mine; // default keep-mine
        break;
      }
    }
  }
  return out;
}
